'use client';

import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import BlogListingCard from '@/app/components/ui/Blog/BlogListingCard';
import LoadingSpinner from '@/app/components/ui/Loader/LoadingSpinner';
import { BlogListingCardProps } from '@/app/types/contentful/types';

// Animation variants for staggered children animation
const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.03, // Slight delay between each child animation
    },
  },
} as const;

export default function ClientNextUp({ blogsCollection, filteredAndSortedBlogs }: { blogsCollection: any; filteredAndSortedBlogs: any }) {

  // Add state for displayed items count
  const [displayedItemsCount, setDisplayedItemsCount] = useState(3);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // Add effect to load more items when scrolling
  useEffect(() => {
    if (inView && displayedItemsCount < filteredAndSortedBlogs.length) {
      setDisplayedItemsCount(prev => Math.min(prev + 3, filteredAndSortedBlogs.length));
    }
  }, [inView, filteredAndSortedBlogs.length, displayedItemsCount]);

  return (
    <>
      <motion.article
        layout="position"
        variants={stagger}
        initial="initial"
        animate="animate"
        className={clsx('col-span-full grid grid-cols-12 gap-x-8 gap-y-20 md:row-start-2 sm:gap-x-0 sm:gap-y-15')}
      >
        {filteredAndSortedBlogs.length > 0 ? (
          <AnimatePresence>
            {filteredAndSortedBlogs.slice(0, displayedItemsCount).map((card: BlogListingCardProps, index: number) => (
              <BlogListingCard key={`${card.sys.id}-${index}`} card={card} initiallyVisible={index < 3} />
            ))}
          </AnimatePresence>
        ) : (
          <>
            <p className="col-span-full text-Body text-GREEN-500">In the meantime, check some of our other blogs</p>
            <AnimatePresence>
              {blogsCollection.slice(0, 12).map((card: BlogListingCardProps, index: number) => (
                <BlogListingCard key={`${card.sys.id}-${index}`} card={card} initiallyVisible={index < 3} />
              ))}
            </AnimatePresence>
          </>
        )}
      </motion.article>

      {displayedItemsCount < filteredAndSortedBlogs.length && (
        <div ref={ref} className="relative col-span-full row-start-3 h-20">
          <LoadingSpinner className="mx-auto h-10 w-10" />
        </div>
      )}
    </>
  );
}
